import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { AlertTriangle, Plus } from "lucide-react";
import { useWarehousePermissions } from "@/hooks/useWarehousePermissions";

const ISSUE_TYPES = [
  "Damaged Package", "Missing Label", "QR Code Not Scannable",
  "Wrong Package Information", "Package Count Mismatch", "Other",
];

export default function WarehouseIssues() {
  const { can } = useWarehousePermissions();
  const canReport = can("report_issue");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ package_id: "", qr_code_id: "", issue_type: ISSUE_TYPES[0], issue_notes: "" });

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/warehouse/issues")).data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/warehouse/issues", form);
      toast.success("Issue reported");
      setOpen(false);
      setForm({ package_id: "", qr_code_id: "", issue_type: ISSUE_TYPES[0], issue_notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="wh-issues-page" className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-1">/ Tracking</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Warehouse Issues</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">Damaged, mislabelled, or otherwise problematic packages.</p>
        </div>
        {canReport && (
          <Button data-testid="wh-issue-new" onClick={() => setOpen(true)} className="bg-cyan-400 text-slate-950 hover:bg-cyan-300">
            <Plus className="w-4 h-4 mr-2" /> Report Issue
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-14 text-center text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-lg py-14 text-center bg-slate-100/30 dark:bg-slate-900/30 text-slate-500">
          <AlertTriangle className="w-10 h-10 text-slate-700 mx-auto mb-3" /> No issues reported.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map(i => (
            <div key={i.id} data-testid={`wh-issue-${i.id}`} className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display font-semibold text-amber-300">{i.issue_type}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 font-mono-tech mt-0.5">
                    {i.package_id || "—"} · {i.qr_code_id || "—"} · {new Date(i.created_at).toLocaleString()}
                  </div>
                </div>
                <span className="text-[10px] font-mono-tech uppercase tracking-wider px-2 py-0.5 rounded border bg-red-500/10 text-red-300 border-red-500/30">
                  {i.status}
                </span>
              </div>
              {i.issue_notes && <div className="text-sm text-slate-700 dark:text-slate-300 mt-2">{i.issue_notes}</div>}
              <div className="text-[11px] text-slate-500 mt-2">by {i.reported_by_name}</div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
          <DialogHeader>
            <DialogTitle>Report Warehouse Issue</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Package ID">
                <Input data-testid="wh-issue-pkg" value={form.package_id} onChange={e => setForm({ ...form, package_id: e.target.value })} placeholder="DBX-XXXXX" className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
              </Field>
              <Field label="QR Code ID">
                <Input data-testid="wh-issue-qr" value={form.qr_code_id} onChange={e => setForm({ ...form, qr_code_id: e.target.value })} placeholder="QR-XXXXXXXX" className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
              </Field>
            </div>
            <Field label="Issue Type">
              <select
                data-testid="wh-issue-type"
                value={form.issue_type}
                onChange={e => setForm({ ...form, issue_type: e.target.value })}
                className="w-full h-9 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md px-3 text-sm"
              >
                {ISSUE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Notes">
              <Textarea data-testid="wh-issue-notes" rows={3} value={form.issue_notes} onChange={e => setForm({ ...form, issue_notes: e.target.value })} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white">Cancel</Button>
            <Button data-testid="wh-issue-submit" onClick={submit} disabled={saving} className="bg-cyan-400 text-slate-950 hover:bg-cyan-300">{saving ? "Saving…" : "Report"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">{label}</Label>
      {children}
    </div>
  );
}
